import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { ContentSections, type ContentSection } from "@/components/anchor/ContentSections";
import { CTAStrip } from "@/components/anchor/CTAStrip";

interface PageSectionRow {
  id: string;
  name: string;
  section_type: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  image_url: string | null;
  image_credit: string | null;
  image_credit_url: string | null;
  link_url: string | null;
  link_label: string | null;
  form_id: string | null;
  cta_keys: string[];
  cta_variant: string;
}

interface Assignment {
  section_id: string;
  sort_order: number;
}

// Pages that already render their own content_sections from page_settings
// (anchor-template pages) — skip fetching page_settings for these to avoid duplication.
const ANCHOR_TEMPLATE_ROUTES = [
  "/",
  "/products",
  "/solutions",
  "/solutions-index",
  "/partners",
  "/contact",
  "/industries",
  "/about",
  "/solutions/enterprise-text-messaging",
];

// Routes that manage their own sections/forms entirely
const SELF_MANAGED_ROUTES = ["/contact"];

function slugFromPath(path: string): string {
  const cleaned = path.replace(/^\/+|\/+$/g, "");
  return cleaned || "home";
}

/**
 * Drop <PageSections /> on any page to auto-render sections assigned to that URL.
 *
 * Props:
 *  - position: "top" | "bottom" (legacy, for pages without slot support)
 *  - slot: a named slot id (e.g. "after-hero") — renders only when
 *          the page's sections_position matches this slot
 */
export default function PageSections({
  position = "bottom",
  slot,
}: {
  position?: "top" | "bottom";
  slot?: string;
}) {
  const location = useLocation();

  // Normalise trailing slashes so "/contact/" behaves exactly like "/contact"
  const pathname = location.pathname.replace(/\/+$/, "") || "/";

  if (SELF_MANAGED_ROUTES.includes(pathname)) return null;

  const isCampaignPage = pathname.startsWith("/c/");
  const isAnchorPage = ANCHOR_TEMPLATE_ROUTES.includes(pathname) || isCampaignPage;

  // --- Universal sections (page_section_assignments) ---
  const { data: assignments } = useQuery({
    queryKey: ["page-section-assignments", pathname],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_section_assignments" as any)
        .select("section_id, sort_order")
        .eq("page_url", pathname)
        .order("sort_order");
      if (error) throw error;
      return data as unknown as Assignment[];
    },
  });

  const sectionIds = (assignments ?? []).map((a) => a.section_id);

  const { data: sections } = useQuery({
    queryKey: ["page-sections", sectionIds],
    queryFn: async () => {
      if (!sectionIds.length) return [];
      const { data, error } = await supabase
        .from("page_sections" as any)
        .select("*")
        .eq("active", true)
        .in("id", sectionIds);
      if (error) throw error;
      return data as unknown as PageSectionRow[];
    },
    enabled: sectionIds.length > 0,
  });

  // --- page_settings.content_sections (for non-anchor pages) ---
  const slug = slugFromPath(pathname);

  const { data: pageSettings } = useQuery({
    queryKey: ["page-settings-sections", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_settings" as any)
        .select("content_sections, sections_position")
        .eq("page_slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as { content_sections: any[] | null; sections_position: string | null } | null;
    },
    enabled: !isAnchorPage,
  });

  // Determine if this instance should render based on position/slot
  const configuredPosition = pageSettings?.sections_position || "bottom";

  if (slot) {
    // Slot mode: only render when the configured position matches this slot
    if (configuredPosition !== slot) return null;
  } else {
    // Legacy position mode: only render for top/bottom
    if (configuredPosition !== position) return null;
  }

  const rendered: React.ReactNode[] = [];

  // 1) Render page_settings content_sections (only for non-anchor pages)
  if (!isAnchorPage && pageSettings?.content_sections?.length) {
    const mapped: ContentSection[] = pageSettings.content_sections.map((s: any) => ({
      title: s.title || "",
      subtitle: s.subtitle || undefined,
      description: s.description || "",
      image_url: s.image_url || undefined,
      image_credit: s.image_credit || undefined,
      image_credit_url: s.image_credit_url || undefined,
      link_url: s.link_url || undefined,
      link_label: s.link_label || undefined,
      cta_key: s.cta_key || undefined,
    }));
    rendered.push(<ContentSections key="ps-content" sections={mapped} />);
  }

  // 2) Render universal sections (page_section_assignments)
  if (assignments?.length && sections?.length) {
    const sortedSections = [...sections].sort((a, b) => {
      const aOrder = assignments.find((x) => x.section_id === a.id)?.sort_order ?? 0;
      const bOrder = assignments.find((x) => x.section_id === b.id)?.sort_order ?? 0;
      return aOrder - bOrder;
    });

    let contentBuffer: ContentSection[] = [];

    const flushContent = () => {
      if (contentBuffer.length > 0) {
        rendered.push(
          <ContentSections key={`content-${rendered.length}`} sections={[...contentBuffer]} />
        );
        contentBuffer = [];
      }
    };

    for (const section of sortedSections) {
      if (section.section_type === "content") {
        contentBuffer.push({
          title: section.title || "",
          subtitle: section.subtitle || undefined,
          description: section.description || "",
          image_url: section.image_url || undefined,
          image_credit: section.image_credit || undefined,
          image_credit_url: section.image_credit_url || undefined,
          link_url: section.link_url || undefined,
          link_label: section.link_label || undefined,
        });
      } else if (section.section_type === "cta_strip") {
        flushContent();
        rendered.push(
          <CTAStrip
            key={section.id}
            buttons={section.cta_keys || []}
            variant={section.cta_variant === "dark" ? "dark" : "light"}
          />
        );
      } else if (section.section_type === "form") {
        flushContent();
        if (section.form_id) {
          rendered.push(<FormSection key={section.id} formId={section.form_id} />);
        }
      }
    }
    flushContent();
  }

  if (rendered.length === 0) return null;

  return <>{rendered}</>;
}

/** Renders a single form by ID */
function FormSection({ formId }: { formId: string }) {
  const { data: form } = useQuery({
    queryKey: ["section-form", formId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_forms" as any)
        .select("*")
        .eq("id", formId)
        .eq("active", true)
        .single();
      if (error) return null;
      return data as any;
    },
  });

  if (!form) return null;

  return (
    <section className="section-padding bg-muted">
      <div className="container-padding mx-auto max-w-xl">
        <div className="bg-card rounded-2xl p-8 shadow-lg">
          {form.heading && (
            <h3 className="text-xl font-semibold text-foreground mb-2">{form.heading}</h3>
          )}
          {form.description && (
            <p className="text-muted-foreground mb-6">{form.description}</p>
          )}
          <div className="space-y-4">
            <input disabled placeholder="Your name *" className="w-full rounded-lg border border-input bg-muted/50 px-3 py-2 text-sm" />
            <input disabled placeholder="Email address *" className="w-full rounded-lg border border-input bg-muted/50 px-3 py-2 text-sm" />
            {form.show_company && (
              <input disabled placeholder="Company" className="w-full rounded-lg border border-input bg-muted/50 px-3 py-2 text-sm" />
            )}
            {form.show_phone && (
              <input disabled placeholder="Phone" className="w-full rounded-lg border border-input bg-muted/50 px-3 py-2 text-sm" />
            )}
            {form.show_message && (
              <textarea disabled placeholder="Message" rows={2} className="w-full rounded-lg border border-input bg-muted/50 px-3 py-2 text-sm resize-none" />
            )}
            <button disabled className="w-full rounded-lg bg-accent text-accent-foreground py-2 text-sm font-semibold opacity-80">
              {form.button_label || "Submit"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
